/* Admin Center — CSV Import tab (extracted from AdminPage.tsx) */

import { useState } from 'react';
import { Upload } from 'lucide-react';
import { Icon } from '../../components/ui/Icon';
import { SearchableSelect } from '../../components/SearchableSelect';
import { rem } from '../../lib/rem';
import { AdminService } from '../../services/adminService';
import { TeamService } from '../../services/teamService';
import { OrgService } from '../../services/orgService';
import { ProjectService } from '../../services/projectService';
import {
  ProjectDesignBriefService,
  DesignBriefFieldService,
} from '../../services/projectDesignBriefService';
import { splitName } from '../people/styles';
import type { AdminPageProps } from './types';
import { inputStyle, btnPrimary, btnDanger, thStyle, tdStyle } from './shared';

/* ─── CSV Import Tab ─── */
function ImportTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [importType, setImportType] = useState<'team' | 'projects' | 'clients'>('team');
  const [csvData, setCsvData] = useState<{
    headers: string[];
    rows: Record<string, string>[];
  } | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);

  const fieldsByType: Record<string, string[]> = {
    team: ['name', 'email', 'role', 'hourlyRate', 'active'],
    projects: [
      'name',
      'projectNumber',
      'clientName',
      'projectType',
      'feeStructure',
      'billingCode',
      'status',
      'description',
    ],
    clients: [
      'companyName',
      'firstName',
      'lastName',
      'address',
      'city',
      'state',
      'zip',
      'rwbPoc',
      'notes',
    ],
  };
  const expectedByType: Record<string, string> = {
    team: 'Expected columns: Name, Email, Role, Hourly Rate, Active',
    projects:
      'Expected columns: Name, Project Number, Client, Project Type, Fee Structure, Billing Code, Status, Description',
    clients:
      'Expected: Company Name, First Name, Last Name, Address, City, State, Zip, RWB POC, Notes. Rows are grouped by Company Name.',
  };

  async function pickFile() {
    const filePath = await window.api.openCsvDialog();
    if (!filePath) return;
    const data = await window.api.parseCsvFile(filePath);
    setCsvData(data);
    const fields = fieldsByType[importType];
    const autoMap: Record<string, string> = {};
    // Aliases for common column name variations
    const aliases: Record<string, string[]> = {
      companyName: ['companyname', 'company', 'name', 'firm', 'client'],
      firstName: ['firstname', 'first', 'fname'],
      lastName: ['lastname', 'last', 'lname'],
      address: ['address', 'streetaddress', 'street', 'addr'],
      rwbPoc: ['rwbpoc', 'rwb', 'poc', 'principle'],
      notes: ['notes', 'note', 'comments'],
      clientName: ['clientname', 'client', 'clientorg', 'organization', 'org'],
      projectType: ['projecttype', 'type', 'projtype'],
      feeStructure: ['feestructure', 'billingtype', 'billing', 'billtype', 'fee'],
      billingCode: ['billingcode', 'activitycode', 'activity', 'description', 'actcode'],
    };
    for (const f of fields) {
      const norms = aliases[f] || [f.toLowerCase()];
      const match = data.headers.find((h) => {
        const hn = h.toLowerCase().replace(/[_\s-]/g, '');
        return norms.some((n) => hn === n);
      });
      if (match) autoMap[f] = match;
    }
    setMapping(autoMap);
  }

  async function doImport() {
    if (!csvData) return;
    setImporting(true);
    let count = 0;
    try {
      if (importType === 'team') {
        for (const row of csvData.rows) {
          const name = row[mapping['name']] || '';
          if (!name.trim()) continue;
          // Boundary parse: the CSV only ever offers one "Name" column, but
          // team_members' split storage (member_profiles) still needs
          // first/middle/last — split it the same way the migration 038
          // backfill splits legacy `name` values (No Full-Name Input/Write
          // Law, 2026-08-19 — CSV import is an external-data boundary, not
          // a typed full-name UI field).
          const nameParts = splitName(name.trim());
          await TeamService.save({
            id: '',
            name: name.trim(),
            firstName: nameParts.firstName,
            middleName: nameParts.middleName,
            lastName: nameParts.lastName,
            email: row[mapping['email']] || '',
            role: row[mapping['role']] || '',
            hourlyRate:
              mapping['hourlyRate'] && row[mapping['hourlyRate']]
                ? parseFloat(row[mapping['hourlyRate']])
                : undefined,
            active: mapping['active']
              ? (row[mapping['active']] || '').toLowerCase() !== 'false' &&
                row[mapping['active']] !== '0'
              : true,
            createdAt: '',
            updatedAt: '',
          });
          count++;
        }
      } else if (importType === 'projects') {
        // Pre-load lookup tables for matching by name
        const [allOrgs, allPTypes, allBTypes, allACodes] = await Promise.all([
          OrgService.list(),
          AdminService.projectTypes.list(),
          AdminService.billingTypes.list(),
          AdminService.activityCodes.list(),
        ]);
        const findOrg = (name: string) => {
          if (!name) return null;
          const n = name.toLowerCase().trim();
          return allOrgs.find((o) => o.name.toLowerCase() === n)?.id || null;
        };
        const findLookup = (list: { id: string; name: string }[], name: string) => {
          if (!name) return null;
          const n = name.toLowerCase().trim();
          return list.find((x) => x.name.toLowerCase() === n)?.id || null;
        };

        for (const row of csvData.rows) {
          const name = row[mapping['name']] || '';
          if (!name.trim()) continue;
          const status = (row[mapping['status']] || '').toLowerCase();
          const clientStr = (row[mapping['clientName']] || '').trim();
          const clientOrgId = findOrg(clientStr);
          const projectTypeId = findLookup(allPTypes, (row[mapping['projectType']] || '').trim());
          const billingTypeId = findLookup(allBTypes, (row[mapping['feeStructure']] || '').trim());
          const activityCodeId = findLookup(allACodes, (row[mapping['billingCode']] || '').trim());
          const savedProject = await ProjectService.save({
            id: '',
            name: name.trim(),
            projectNumber: row[mapping['projectNumber']] || undefined,
            clientOrgId: clientOrgId || undefined,
            clientName: clientStr || undefined,
            projectTypeId: projectTypeId || undefined,
            billingTypeId: billingTypeId || undefined,
            activityCodeId: activityCodeId || undefined,
            status: status === 'closed' ? 'closed' : 'active',
            selections: {},
            createdAt: '',
            updatedAt: '',
          });
          // No more `Project.description` (migration 055) — the CSV
          // "description" column now lands on the new project's design
          // brief: its scope narrative when scope is still empty, else
          // (an existing brief already carrying a different scope — not
          // possible for a brand-new project today, but the importer
          // stays correct if a future pass starts matching existing
          // projects) preserved as a supplemental "Description" field.
          const descriptionText = (row[mapping['description']] || '').trim();
          if (descriptionText) {
            const existingBrief = await ProjectDesignBriefService.getByProject(savedProject.id);
            if (existingBrief && existingBrief.scope && existingBrief.scope.trim()) {
              await DesignBriefFieldService.save({
                id: '',
                projectId: savedProject.id,
                title: 'Description',
                value: descriptionText,
                sortOrder: 0,
                createdAt: '',
                updatedAt: '',
              });
            } else {
              await ProjectDesignBriefService.save({
                ...(existingBrief || {
                  id: '',
                  projectId: savedProject.id,
                  applicableCodes: [],
                  createdAt: '',
                  updatedAt: '',
                }),
                scope: descriptionText,
              });
            }
          }
          count++;
        }
      } else if (importType === 'clients') {
        // Group rows by company name
        const grouped = new Map<string, typeof csvData.rows>();
        for (const row of csvData.rows) {
          const company = (row[mapping['companyName']] || '').trim();
          if (!company) continue;
          if (!grouped.has(company)) grouped.set(company, []);
          grouped.get(company)!.push(row);
        }
        for (const [company, rows] of grouped) {
          // Collect contacts from all rows
          const contacts: { name: string; title?: string; email?: string; phone?: string }[] = [];
          for (const row of rows) {
            const first = (row[mapping['firstName']] || '').trim();
            const last = (row[mapping['lastName']] || '').trim();
            if (first || last) contacts.push({ name: [first, last].filter(Boolean).join(' ') });
          }
          // Dedupe addresses
          const addrSet = new Set<string>();
          const addresses: {
            label?: string;
            address: string;
            city: string;
            state: string;
            zip: string;
          }[] = [];
          for (const row of rows) {
            const street = (row[mapping['address']] || '').trim();
            const city = (row[mapping['city']] || '').trim();
            const state = (row[mapping['state']] || '').trim();
            const zip = (row[mapping['zip']] || '').trim();
            if (!street && !city) continue;
            const key = `${street}|${city}|${state}|${zip}`;
            if (addrSet.has(key)) continue;
            addrSet.add(key);
            addresses.push({ address: street, city, state, zip });
          }
          // RWB POC + notes from first row that has them
          const rwbPoc =
            rows.find((r) => (r[mapping['rwbPoc']] || '').trim())?.[mapping['rwbPoc']]?.trim() ||
            '';
          const notes =
            rows.find((r) => (r[mapping['notes']] || '').trim())?.[mapping['notes']]?.trim() || '';
          await OrgService.save({
            id: '',
            name: company,
            type: 'client',
            addresses,
            contacts,
            rwbPoc,
            notes,
            createdAt: '',
            updatedAt: '',
          });
          count++;
        }
      }
      const labels: Record<string, string> = {
        team: 'team members',
        projects: 'projects',
        clients: 'clients',
      };
      toast(`Imported ${count} ${labels[importType]}`);
      setCsvData(null);
      setMapping({});
    } catch (err: any) {
      toast('Import error: ' + err.message, true);
    }
    setImporting(false);
  }

  const fields = fieldsByType[importType];
  const importLabels: Record<string, string> = {
    team: 'Members',
    projects: 'Projects',
    clients: 'Clients',
  };

  return (
    <div>
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          color: 'var(--tm)',
          marginBottom: 'var(--space-4)',
        }}
      >
        Import team members, projects, or clients from a CSV file. Great for migrating from Ajera or
        other systems.
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            fontWeight: 600,
            color: 'var(--tm)',
          }}
        >
          Import:
        </div>
        <div
          style={{
            display: 'flex',
            gap: rem(2),
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            padding: rem(2),
          }}
        >
          {(['team', 'projects', 'clients'] as const).map((t) => (
            <button
              key={t}
              onClick={() => {
                setImportType(t);
                setCsvData(null);
              }}
              style={{
                padding: `${rem(5)} ${rem(14)}`,
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                fontWeight: 600,
                cursor: 'pointer',
                background: importType === t ? 'var(--sf)' : 'transparent',
                color: importType === t ? 'var(--tx)' : 'var(--tm)',
                boxShadow: importType === t ? 'var(--shadow-sm)' : 'none',
              }}
            >
              {t === 'team' ? 'Team Members' : t === 'projects' ? 'Projects' : 'Clients'}
            </button>
          ))}
        </div>
      </div>

      {!csvData ? (
        <div
          style={{
            textAlign: 'center',
            padding: `${rem(40)} ${rem(20)}`,
            background: 'var(--sf)',
            borderRadius: 'var(--radius-lg)',
            border: '2px dashed var(--bd)',
            cursor: 'pointer',
          }}
          onClick={pickFile}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              opacity: 0.3,
              marginBottom: 'var(--space-2)',
              color: 'var(--tm)',
            }}
            aria-hidden
          >
            <Icon icon={Upload} em={32 / 13} strokeWidth={1.5} />
          </div>
          <div
            style={{
              fontSize: 'calc(var(--font-size-base) * 14 / 13)',
              fontWeight: 600,
              marginBottom: 'var(--space-1)',
            }}
          >
            Click to select a CSV file
          </div>
          <div style={{ fontSize: 'calc(var(--font-size-base) * 12 / 13)', color: 'var(--tm)' }}>
            {expectedByType[importType]}
          </div>
        </div>
      ) : (
        <div>
          <div
            style={{
              marginBottom: 'var(--space-4)',
              padding: 'var(--space-3)',
              background: 'var(--sf)',
              borderRadius: rem(8),
              border: '1px solid var(--bd)',
            }}
          >
            <div
              style={{
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                fontWeight: 700,
                marginBottom: 'var(--space-2)',
              }}
            >
              Found {csvData.rows.length} rows with {csvData.headers.length} columns
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                marginBottom: 'var(--space-3)',
              }}
            >
              Map your CSV columns to SpecForma fields:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
              {fields.map((f) => (
                <div
                  key={f}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                >
                  <span
                    style={{
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      fontWeight: 600,
                      width: rem(100),
                    }}
                  >
                    {f}:
                  </span>
                  <div style={{ flex: 1 }}>
                    <SearchableSelect
                      value={mapping[f] || ''}
                      options={csvData.headers.map((h) => ({ value: h, label: h }))}
                      onChange={(v) => setMapping((prev) => ({ ...prev, [f]: v }))}
                      placeholder="-- skip --"
                      searchPlaceholder="Search columns…"
                      style={{ ...inputStyle, fontSize: 'calc(var(--font-size-base) * 12 / 13)' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              marginBottom: 'var(--space-4)',
              maxHeight: rem(200),
              overflow: 'auto',
              background: 'var(--sf)',
              borderRadius: rem(8),
              border: '1px solid var(--bd)',
            }}
          >
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            >
              <thead>
                <tr>
                  {csvData.headers.map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvData.rows.slice(0, 5).map((row, i) => (
                  <tr key={i}>
                    {csvData.headers.map((h) => (
                      <td key={h} style={tdStyle}>
                        {row[h]}
                      </td>
                    ))}
                  </tr>
                ))}
                {csvData.rows.length > 5 && (
                  <tr>
                    <td
                      colSpan={csvData.headers.length}
                      style={{ ...tdStyle, textAlign: 'center', color: 'var(--tm)' }}
                    >
                      ... and {csvData.rows.length - 5} more rows
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              onClick={doImport}
              disabled={importing || !(mapping['name'] || mapping['companyName'])}
              style={{
                ...btnPrimary,
                opacity: importing || !(mapping['name'] || mapping['companyName']) ? 0.5 : 1,
              }}
            >
              {importing
                ? 'Importing...'
                : `Import ${importType === 'clients' ? new Set(csvData.rows.map((r) => (r[mapping['companyName']] || '').trim()).filter(Boolean)).size + ' Clients' : csvData.rows.length + ' ' + importLabels[importType]}`}
            </button>
            <button
              onClick={() => {
                setCsvData(null);
                setMapping({});
              }}
              style={{ ...btnDanger, color: 'var(--tm)', border: '1px solid var(--bd)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { ImportTab };
